import { CheckCircle, Circle, CircleDashed, CircleHalf, XCircle } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { cx } from "@/lib/cx";

export type StatusCategory = "backlog" | "unstarted" | "started" | "completed" | "canceled";

export const STATUS_CATEGORIES: StatusCategory[] = ["backlog", "unstarted", "started", "completed", "canceled"];

export function statusCategoryLabel(c: StatusCategory): string {
  switch (c) {
    case "backlog":
      return m.status_backlog();
    case "unstarted":
      return m.status_unstarted();
    case "started":
      return m.status_started();
    case "completed":
      return m.status_completed();
    case "canceled":
      return m.status_canceled();
  }
}

const color: Record<StatusCategory, string> = {
  backlog: "text-status-backlog",
  unstarted: "text-status-unstarted",
  started: "text-status-started",
  completed: "text-status-completed",
  canceled: "text-status-canceled",
};

/**
 * Status glyph. Shape carries the category (dashed ring, empty ring, half disc, check disc, cross disc),
 * colour only reinforces it. `label` should be the user-defined status name when known; it falls back to
 * the category name. Set `decorative` when the name is printed right next to it.
 */
export function StatusIcon({
  category,
  size = 14,
  label,
  decorative,
  className,
}: {
  category: StatusCategory;
  size?: number;
  label?: string;
  decorative?: boolean;
  className?: string;
}) {
  const props = { size, className: cx("shrink-0", color[category], className) };
  const glyph =
    category === "backlog" ? (
      <CircleDashed {...props} aria-hidden="true" />
    ) : category === "unstarted" ? (
      <Circle {...props} aria-hidden="true" />
    ) : category === "started" ? (
      <CircleHalf {...props} weight="fill" aria-hidden="true" />
    ) : category === "completed" ? (
      <CheckCircle {...props} weight="fill" aria-hidden="true" />
    ) : (
      <XCircle {...props} weight="fill" aria-hidden="true" />
    );
  if (decorative) return glyph;
  return (
    <span role="img" aria-label={label ?? statusCategoryLabel(category)} className="inline-flex shrink-0">
      {glyph}
    </span>
  );
}
